/** Patient billing and chronic condition display helpers. */

/** Single chronic condition entry → display label (handles ICD objects or plain strings). */
export function formatChronicConditionEntry(entry) {
  if (entry == null || entry === '') return '';
  if (typeof entry === 'object') {
    const code = entry.icd_code || entry.code || '';
    const desc = entry.condition_name || entry.icd_description || entry.description || '';
    if (!code) return String(desc).trim();
    return desc && desc !== code ? `${code} - ${desc}` : String(code);
  }
  return String(entry).trim();
}

function tryDecodeJsonString(value) {
  let current = value;
  for (let i = 0; i < 3; i += 1) {
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) return null;
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{') && !trimmed.startsWith('"')) return null;
    try {
      current = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return current;
}

/** Parse icd_codes / chronic_conditions into raw entries (strings or objects). */
export function parseRawChronicConditionItems(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw;

  const decoded = tryDecodeJsonString(raw);
  if (decoded != null && decoded !== raw) {
    return parseRawChronicConditionItems(decoded);
  }

  const str = String(raw).trim();
  if (!str) return [];

  if (str.startsWith('[') || str.startsWith('{')) {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') return [parsed];
    } catch {
      const names = extractConditionNamesFromJsonString(str);
      if (names.length) return names;
    }
  }

  const bracketIdx = str.indexOf('[');
  if (bracketIdx > 0) {
    const plain = str.slice(0, bracketIdx).replace(/,\s*$/, '').trim();
    const jsonPart = str.slice(bracketIdx);
    const items = [];
    if (plain) items.push(plain);
    try {
      const parsed = JSON.parse(jsonPart);
      const parsedItems = Array.isArray(parsed) ? parsed : [parsed];
      items.push(...parsedItems);
    } catch {
      items.push(...extractConditionNamesFromJsonString(jsonPart));
    }
    return items.filter(Boolean);
  }

  if (str.includes('"condition_name"') || str.includes('{') || str.startsWith('[')) {
    const names = extractConditionNamesFromJsonString(str);
    if (names.length) return names;
    return [];
  }

  return str
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s && !s.startsWith('[') && !s.startsWith('{'));
}

function extractConditionNamesFromJsonString(str) {
  const names = [];
  const seen = new Set();
  const re = /"condition_name"\s*:\s*"((?:\\.|[^"\\])*)"/g;
  let match = re.exec(str);
  while (match) {
    const name = match[1].replace(/\\"/g, '"').trim();
    const key = name.toLowerCase();
    if (name && !seen.has(key)) {
      seen.add(key);
      names.push(name);
    }
    match = re.exec(str);
  }

  // Truncated DB values (varchar overflow) may cut off before the closing quote.
  if (names.length <= 1) {
    const partialRe = /"condition_name"\s*:\s*"([^"[{]+)/g;
    let partialMatch = partialRe.exec(str);
    while (partialMatch) {
      const name = partialMatch[1].replace(/\\"/g, '"').trim();
      const key = name.toLowerCase();
      if (name && !seen.has(key)) {
        seen.add(key);
        names.push(name);
      }
      partialMatch = partialRe.exec(str);
    }
  }

  return names;
}

/** Parse icd_codes / chronic_conditions (JSON array, comma string, or array) → string labels. */
export function parseChronicConditions(raw) {
  return parseRawChronicConditionItems(raw).map(formatChronicConditionEntry).filter(Boolean);
}

export function normalizeChronicConditionLabel(label) {
  return String(label ?? '')
    .trim()
    .replace(/^[\s"'[]+|[\s"'\]]+$/g, '');
}

/** Parsed chronic conditions with JSON artifacts removed and duplicates collapsed. */
export function getUniqueChronicConditions(raw) {
  const seen = new Set();
  const result = [];
  for (const entry of parseRawChronicConditionItems(raw)) {
    const label = normalizeChronicConditionLabel(formatChronicConditionEntry(entry));
    if (!label || label.startsWith('[') || label.startsWith('{')) continue;
    if (label.includes('"condition_name"') || label.includes('"icd_description"')) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(label);
  }
  return result;
}

export function formatChronicConditionsDisplay(raw) {
  const list = parseChronicConditions(raw);
  return list.length ? list.join(', ') : '';
}

/** Chronic conditions → disease names only (no ICD codes / raw JSON). */
export function formatChronicDiseaseNames(raw) {
  const names = [];
  const seen = new Set();
  for (const entry of parseRawChronicConditionItems(raw)) {
    let name = '';
    if (entry && typeof entry === 'object') {
      name = String(entry.condition_name || entry.name || '').trim();
      if (!name) {
        const desc = String(entry.icd_description || entry.description || '').trim();
        const codePrefixed = desc.match(/^[A-Z]\d[\w.]*\s*[-–:]\s*(.+)$/i);
        name = codePrefixed ? codePrefixed[1].trim() : desc;
      }
    } else {
      name = String(entry || '').trim();
      const codePrefixed = name.match(/^[A-Z]\d[\w.]*\s*[-–:]\s*(.+)$/i);
      if (codePrefixed) name = codePrefixed[1].trim();
    }
    if (!name || name.startsWith('[') || name.startsWith('{')) continue;
    if (
      name.includes('"condition_name"') ||
      name.includes('"icd_description"') ||
      name.includes('"icd_code"')
    ) {
      continue;
    }
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names.length ? names.join(', ') : 'N/A';
}

export const CCM_BILLING_PATH_LABELS = {
  clinical_complex: 'Complex CCM — clinical staff path (99487 / 99489)',
  provider_non_complex: 'Non-Complex — provider personally (99491 / 99437)',
  clinical_non_complex: 'Non-Complex — clinical staff (99490 / 99439)',
  none: 'Not billable yet — insufficient time this period',
  blocked: 'Billing blocked — resolve conflicts first'
};

export function getCcmBillingPathLabel(billingPath) {
  const key = String(billingPath || '').trim();
  return CCM_BILLING_PATH_LABELS[key] || (key ? key : '—');
}

export function getCcmComplexityMinuteTarget(isComplex) {
  return isComplex ? 60 : 20;
}

export function splitCptPillsIntoRows(pills, program = 'RPM') {
  const list = Array.isArray(pills) ? pills : [];
  if (list.length === 0) return { top: [], bottom: [] };

  if (program === 'CCM') {
    const primary = ['99490', '99491', '99439', '99437'];
    const complex = ['99487', '99489'];
    const top = list.filter((p) => primary.includes(p.code));
    const bottom = list.filter((p) => complex.includes(p.code));
    if (bottom.length === 0 && top.length > 5) {
      return { top: top.slice(0, 5), bottom: top.slice(5) };
    }
    return { top, bottom };
  }

  const top = list.slice(0, 5);
  const bottom = list.slice(5);
  return { top, bottom };
}

export function renderCptPillStatus(pill) {
  if (!pill) return null;
  if (pill.icon) return { type: 'icon', value: pill.icon };
  if (pill.status) {
    const lines = String(pill.status).split('\n');
    return { type: 'status', lines };
  }
  return null;
}

export const DEFAULT_PROGRAM_TIMERS = {
  RPM: { seconds: 0, isRunning: false },
  CCM: { seconds: 0, isRunning: false },
  PCM: { seconds: 0, isRunning: false }
};

const PROFILE_TIMER_PROGRAMS = ['RPM', 'CCM', 'PCM'];

function emptyProgramTimerMap() {
  return {
    RPM: { ...DEFAULT_PROGRAM_TIMERS.RPM },
    CCM: { ...DEFAULT_PROGRAM_TIMERS.CCM },
    PCM: { ...DEFAULT_PROGRAM_TIMERS.PCM }
  };
}

/** sessionStorage key shared by patient profile + measurement screens. */
export function getPatientTimerStorageKey(practiceId, patientId) {
  return `pd-timers-${practiceId}-${patientId}`;
}

/**
 * True while still inside this patient's session screens
 * (profile, measurements, follow-ups, edit) — not the patients list.
 */
export function isSamePatientSessionPath(pathname, practiceId, patientId) {
  if (!pathname || practiceId == null || patientId == null) return false;
  const marker = `/practices/${practiceId}/patients/${patientId}`;
  return pathname === marker || pathname.startsWith(`${marker}/`);
}

/**
 * Persist when navigating within the same patient session; otherwise clear
 * so returning via the patients list starts a fresh timer.
 */
export function finalizePatientSessionTimers(storageKey, timers, { practiceId, patientId, pathname } = {}) {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  if (isSamePatientSessionPath(path, practiceId, patientId)) {
    persistTimers(storageKey, timers);
    return;
  }
  clearPersistedTimers(storageKey);
}

function normalizeProgramTimerEntry(entry = {}) {
  return {
    seconds: Math.max(0, Number(entry.seconds) || 0),
    isRunning: Boolean(entry.isRunning),
    updatedAt: entry.updatedAt != null ? Number(entry.updatedAt) : null
  };
}

function toUiProgramTimers(timers = {}) {
  return {
    RPM: {
      seconds: Math.max(0, Number(timers?.RPM?.seconds) || 0),
      isRunning: Boolean(timers?.RPM?.isRunning)
    },
    CCM: {
      seconds: Math.max(0, Number(timers?.CCM?.seconds) || 0),
      isRunning: Boolean(timers?.CCM?.isRunning)
    },
    PCM: {
      seconds: Math.max(0, Number(timers?.PCM?.seconds) || 0),
      isRunning: Boolean(timers?.PCM?.isRunning)
    }
  };
}

/**
 * If a program timer was running when the screen unmounted, advance seconds
 * by wall-clock elapsed so navigation does not freeze/lose time.
 */
export function reconcileProgramTimers(timers = {}) {
  const now = Date.now();
  const reconcile = (entry) => {
    const next = normalizeProgramTimerEntry(entry);
    if (next.isRunning && Number.isFinite(next.updatedAt) && next.updatedAt > 0) {
      const elapsedSec = Math.floor((now - next.updatedAt) / 1000);
      if (elapsedSec > 0) {
        next.seconds += elapsedSec;
      }
      next.updatedAt = now;
    }
    return next;
  };

  return {
    RPM: reconcile(timers.RPM),
    CCM: reconcile(timers.CCM),
    PCM: reconcile(timers.PCM),
    activeProgram: PROFILE_TIMER_PROGRAMS.includes(String(timers.activeProgram || '').toUpperCase())
      ? String(timers.activeProgram).toUpperCase()
      : null
  };
}

/** Fresh in-memory timer state (not restored from storage). */
export function createFreshProgramTimers() {
  return emptyProgramTimerMap();
}

export function loadPersistedTimers(storageKey) {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return { ...createFreshProgramTimers(), activeProgram: null };
    }
    const parsed = JSON.parse(raw);
    const reconciled = reconcileProgramTimers(parsed);
    return {
      ...toUiProgramTimers(reconciled),
      activeProgram: reconciled.activeProgram
    };
  } catch {
    return { ...createFreshProgramTimers(), activeProgram: null };
  }
}

export function persistTimers(storageKey, timers) {
  try {
    const now = Date.now();
    const stamp = (entry) => {
      const seconds = Math.max(0, Number(entry?.seconds) || 0);
      const isRunning = Boolean(entry?.isRunning);
      return {
        seconds,
        isRunning,
        // Stamp wall clock while running so the next screen can catch up.
        updatedAt: isRunning ? now : null
      };
    };
    const activeProgram = PROFILE_TIMER_PROGRAMS.includes(String(timers?.activeProgram || '').toUpperCase())
      ? String(timers.activeProgram).toUpperCase()
      : null;
    const payload = JSON.stringify({
      RPM: stamp(timers?.RPM),
      CCM: stamp(timers?.CCM),
      PCM: stamp(timers?.PCM),
      ...(activeProgram ? { activeProgram } : {})
    });
    sessionStorage.setItem(storageKey, payload);
  } catch {
    // ignore quota errors
  }
}

export function clearPersistedTimers(storageKey) {
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

export function bindTimerPersistOnPageHide() {
  return () => {};
}

/** Prefer route state, then stored active program, then whichever timer is running. */
export function resolveActiveProgramTimerKey({ routeProgram, storedProgram, timers } = {}) {
  const fromRoute = String(routeProgram || '').toUpperCase();
  if (PROFILE_TIMER_PROGRAMS.includes(fromRoute)) return fromRoute;

  const fromStore = String(storedProgram || '').toUpperCase();
  if (PROFILE_TIMER_PROGRAMS.includes(fromStore)) return fromStore;

  if (timers?.RPM?.isRunning) return 'RPM';
  if (timers?.CCM?.isRunning) return 'CCM';
  if (timers?.PCM?.isRunning) return 'PCM';
  return 'RPM';
}

/**
 * Only one program timer runs at a time — matches active RPM/CCM/PCM tab.
 * Dual-enrolled patients keep separate accumulated seconds per program.
 */
export function applyExclusiveProgramTimerState(timers, activeProgram, { practiceIsLocked = false } = {}) {
  if (practiceIsLocked) {
    return emptyProgramTimerMap();
  }

  const prev = {
    RPM: { ...DEFAULT_PROGRAM_TIMERS.RPM, ...(timers?.RPM || {}) },
    CCM: { ...DEFAULT_PROGRAM_TIMERS.CCM, ...(timers?.CCM || {}) },
    PCM: { ...DEFAULT_PROGRAM_TIMERS.PCM, ...(timers?.PCM || {}) }
  };

  const next = {
    RPM: { ...prev.RPM, isRunning: false },
    CCM: { ...prev.CCM, isRunning: false },
    PCM: { ...prev.PCM, isRunning: false }
  };

  const program = String(activeProgram || '').toUpperCase();
  if (PROFILE_TIMER_PROGRAMS.includes(program)) {
    next[program] = { ...next[program], isRunning: true };
  }

  return next;
}

/** RPM billing CPT codes (matches patient details RPM timer pills). */
export const RPM_BILLING_CPT_CODES = ['99453', '99445', '99454', '99470', '99457', '99458'];

/** 99458 add-on units from total RPM service minutes (matches backend billing). */
export function getRpm99458UnitCount(totalServiceMinutes) {
  const mins = Number.parseInt(totalServiceMinutes, 10) || 0;
  if (mins < 40) return 0;
  if (mins < 60) return 1;
  return 2;
}

/**
 * 99458 chip detail — only after 99457 (20 total minutes) is met.
 * Progress is within the current 20-minute add-on tier (not total minutes).
 */
export function getRpm99458ChipDetail(totalServiceMinutes) {
  const minutes = Math.max(0, Number(totalServiceMinutes) || 0);
  if (minutes < 20) {
    return { show: false, units: 0, tierProgress: 0, target: 20 };
  }
  const toward58 = Math.max(0, minutes - 20);
  const units = getRpm99458UnitCount(minutes);
  let tierProgress = toward58 % 20;
  if (tierProgress === 0 && toward58 > 0) {
    tierProgress = 20;
  }
  return { show: true, units, tierProgress, target: 20 };
}

/** CCM billing CPT codes (matches patient details CCM timer pills). */
export const CCM_BILLING_CPT_CODES = ['99490', '99439', '99491', '99437', '99487', '99489'];

/** PCM billing CPT codes (matches patient details PCM timer pills). */
export const PCM_BILLING_CPT_CODES = ['99424', '99425', '99426', '99427'];

export function getBillingCptCodesForProgram(program) {
  const key = String(program || 'rpm').toLowerCase();
  if (key === 'ccm') return CCM_BILLING_CPT_CODES;
  if (key === 'pcm') return PCM_BILLING_CPT_CODES;
  return RPM_BILLING_CPT_CODES;
}
